import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAccountParams } from '~/hooks/useAccountParams';
import {
  type EntityFilterType,
  getEntitiesSuggestions,
  hydrateEntities,
  searchEntities,
} from '~/services/entityFilterApi';

type DateRange = { from: string; to: string };

const STALE_TIME = 60 * 1000;

const CID_PARAM = 'cid';
const PID_PARAM = 'pid';

function parsePositiveIntParams(searchParams: URLSearchParams, name: string): number[] {
  return searchParams
    .getAll(name)
    .map((v) => parseInt(v, 10))
    .filter((n) => !Number.isNaN(n) && n > 0);
}

/**
 * Resolves analytics stats URL `from` / `to` (YYYYMMDD) the same way as when those
 * params are absent from `useCommonParams` defaults (today and 29 days prior).
 */
export function getAnalyticsEntityFilterDateRangeFromSearch(search: string): DateRange {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const defaultTo = dayjs();
  const defaultFrom = defaultTo.subtract(29, 'day');
  const from = params.get('from') || defaultFrom.format('YYYYMMDD');
  const to = params.get('to') || defaultTo.format('YYYYMMDD');
  return { from, to };
}

export function getEntityFilterHydrateQueryKey(
  accountId: number,
  entityType: EntityFilterType,
  selectedIds: number[],
  dateRange: DateRange,
  clientIds?: number[],
): unknown[] {
  return [
    'entity-filter',
    'hydrate',
    entityType,
    accountId,
    selectedIds,
    dateRange.from,
    dateRange.to,
    clientIds,
  ];
}

export function getEntityFilterHydrateQueryOptions({
  accountId,
  entityType,
  selectedIds,
  dateRange,
  clientIds,
  enabled = true,
}: {
  accountId: number;
  entityType: EntityFilterType;
  selectedIds: number[];
  dateRange: DateRange;
  clientIds?: number[];
  enabled?: boolean;
}) {
  return queryOptions({
    queryKey: getEntityFilterHydrateQueryKey(
      accountId,
      entityType,
      selectedIds,
      dateRange,
      clientIds,
    ),
    queryFn: async () => {
      return hydrateEntities(accountId, selectedIds, dateRange.from, entityType, dateRange.to);
    },
    enabled: enabled && !!accountId && selectedIds.length > 0,
    staleTime: STALE_TIME,
  });
}

export function useEntityFilterHydrateQuery(
  entityType: EntityFilterType,
  selectedIds: number[],
  dateRange: DateRange,
  options?: {
    enabled?: boolean;
    clientIds?: number[]; // for products: restrict to these child clients
  },
) {
  const params = useAccountParams();
  const accountId = params?.accountId ?? 0;
  const { enabled = true, clientIds } = options ?? {};

  return useQuery(
    getEntityFilterHydrateQueryOptions({
      accountId,
      entityType,
      selectedIds,
      dateRange,
      clientIds,
      enabled,
    }),
  );
}

export function getEntityFilterSuggestionsQueryKey(
  accountId: number,
  entityType: EntityFilterType,
  dateRange: DateRange,
  limit: number,
  clientIds?: number[],
): unknown[] {
  return [
    'entity-filter',
    'suggestions',
    entityType,
    accountId,
    dateRange.from,
    dateRange.to,
    limit,
    clientIds,
  ];
}

export function getEntityFilterSuggestionsQueryOptions({
  accountId,
  entityType,
  dateRange,
  limit = 50,
  clientIds,
  enabled = true,
}: {
  accountId: number;
  entityType: EntityFilterType;
  dateRange: DateRange;
  limit?: number;
  clientIds?: number[];
  enabled?: boolean;
}) {
  return queryOptions({
    queryKey: getEntityFilterSuggestionsQueryKey(
      accountId,
      entityType,
      dateRange,
      limit,
      clientIds,
    ),
    queryFn: async () =>
      getEntitiesSuggestions(
        accountId,
        dateRange.from,
        dateRange.to,
        entityType,
        limit,
        clientIds?.length ? clientIds : undefined,
      ),
    enabled: enabled && !!accountId && !!dateRange.from && !!dateRange.to,
    staleTime: STALE_TIME,
  });
}

export function useEntityFilterSuggestionsQuery(
  entityType: EntityFilterType,
  dateRange: DateRange,
  options?: {
    enabled?: boolean;
    limit?: number;
    clientIds?: number[]; // for products: restrict to these child clients
  },
) {
  const params = useAccountParams();
  const accountId = params?.accountId ?? 0;
  const { enabled = true, limit = 50, clientIds } = options ?? {};

  return useQuery({
    ...getEntityFilterSuggestionsQueryOptions({
      accountId,
      entityType,
      dateRange,
      limit,
      clientIds,
      enabled,
    }),
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery?.queryKey[2] === entityType) {
        return previousData;
      }
      return undefined;
    },
  });
}

/**
 * Prefetches entity suggestions (and hydrate when `cid` / `pid` are in the URL) for
 * analytics stats builder and saved-view pages, so opening the filter drawer hits cache.
 *
 * @remarks
 * Hydrate/suggestions HTTP endpoints live in `entityFilterApi`. Drawer UI: `EntityFiltersButton`
 * → `AnalyticsEntityFiltersDrawer` → `EntityFilterPicker` → `SuggestedSection` / `SelectedSection`.
 */
export async function prefetchAnalyticsEntityFilterQueries(
  queryClient: QueryClient,
  input: {
    accountId: number;
    /** `location.search` (with or without leading `?`) */
    search: string;
    isParent: boolean;
  },
): Promise<void> {
  const { accountId, isParent } = input;
  if (!accountId) return;

  const dateRange = getAnalyticsEntityFilterDateRangeFromSearch(input.search);
  const params = new URLSearchParams(
    input.search.startsWith('?') ? input.search.slice(1) : input.search,
  );
  const clientIdsFromUrl = parsePositiveIntParams(params, CID_PARAM);
  const productIdsFromUrl = parsePositiveIntParams(params, PID_PARAM);

  const suggestionLimit = 50;

  const productSuggestionClientIds =
    isParent && clientIdsFromUrl.length > 0 ? clientIdsFromUrl : [];

  const prefetchTasks: Promise<void>[] = [
    queryClient.prefetchQuery(
      getEntityFilterSuggestionsQueryOptions({
        accountId,
        entityType: 'clients',
        dateRange,
        limit: suggestionLimit,
        clientIds: [],
      }),
    ),
    queryClient.prefetchQuery(
      getEntityFilterSuggestionsQueryOptions({
        accountId,
        entityType: 'products',
        dateRange,
        limit: suggestionLimit,
        clientIds: productSuggestionClientIds,
      }),
    ),
  ];

  if (clientIdsFromUrl.length > 0) {
    prefetchTasks.push(
      queryClient.prefetchQuery(
        getEntityFilterHydrateQueryOptions({
          accountId,
          entityType: 'clients',
          selectedIds: clientIdsFromUrl,
          dateRange,
          enabled: true,
        }),
      ),
    );
  }

  if (productIdsFromUrl.length > 0) {
    const hydrateProductClientIds =
      isParent && clientIdsFromUrl.length > 0 ? clientIdsFromUrl : undefined;
    prefetchTasks.push(
      queryClient.prefetchQuery(
        getEntityFilterHydrateQueryOptions({
          accountId,
          entityType: 'products',
          selectedIds: productIdsFromUrl,
          dateRange,
          clientIds: hydrateProductClientIds,
          enabled: true,
        }),
      ),
    );
  }

  await Promise.all(prefetchTasks);
}

export function useEntityFilterSearchQuery(
  entityType: EntityFilterType,
  searchQuery: string,
  options?: {
    enabled?: boolean;
    limit?: number;
    clientIds?: number[];
  },
) {
  const params = useAccountParams();
  const accountId = params?.accountId ?? 0;
  const { enabled = true, limit = 20, clientIds } = options ?? {};
  const trimmedQuery = searchQuery.trim();

  return useQuery({
    queryKey: ['entity-filter', 'search', entityType, accountId, trimmedQuery, limit, clientIds],
    queryFn: async () => {
      return searchEntities(accountId, trimmedQuery, entityType, limit);
    },
    enabled: enabled && !!accountId && trimmedQuery.length >= 1 && trimmedQuery.length <= 200,
    staleTime: 30 * 1000,
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery?.queryKey[2] === entityType) {
        return previousData;
      }
      return undefined;
    },
  });
}
